import { useMemo, useState } from 'react'
import {
  FaBriefcase,
  FaCalculator,
  FaCheck,
  FaClipboardList,
  FaFileInvoice,
  FaFileSignature,
  FaFlag,
  FaPlus,
  FaRegCalendar,
} from 'react-icons/fa'

/**
 * A mock-up of the planned task / action list, for showing people what it
 * could look like. Everything here is sample data held in the browser: ticking,
 * adding and filtering work so it feels real, but nothing is saved and the
 * list resets on refresh. There is no task feature behind it yet.
 */

type Kind = 'job' | 'quote' | 'enquiry' | 'invoice' | 'po'
type Priority = 'high' | 'normal' | 'low'

interface Task {
  id: number
  title: string
  /** Days from today: negative is overdue, 0 is today. */
  dueIn: number
  priority: Priority
  kind: Kind
  /** The record it's about - sample references, not real ones. */
  reference: string
  from: string
  done: boolean
}

const KIND_ICON: Record<Kind, JSX.Element> = {
  job: <FaBriefcase />,
  quote: <FaCalculator />,
  enquiry: <FaClipboardList />,
  invoice: <FaFileInvoice />,
  po: <FaFileSignature />,
}

const SAMPLE_TASKS: Task[] = [
  { id: 1, title: 'Chase supplier for the outstanding cable delivery', dueIn: -2, priority: 'high', kind: 'po', reference: 'PO-J004512-002', from: 'Purchasing', done: false },
  { id: 2, title: 'Call the client back about the revised quote', dueIn: -1, priority: 'normal', kind: 'quote', reference: 'Q003871', from: 'You', done: false },
  { id: 3, title: 'Approve the variation before work starts on site', dueIn: 0, priority: 'high', kind: 'job', reference: 'J004498', from: 'Site team', done: false },
  { id: 4, title: 'Book the site survey and confirm access', dueIn: 0, priority: 'normal', kind: 'enquiry', reference: 'E002311', from: 'You', done: false },
  { id: 5, title: 'Check the supplier invoice against the purchase order', dueIn: 1, priority: 'normal', kind: 'invoice', reference: 'INV-88213', from: 'Accounts', done: false },
  { id: 6, title: 'Send the Go/No-Go decision to the estimating team', dueIn: 3, priority: 'low', kind: 'enquiry', reference: 'E002305', from: 'Estimating', done: false },
  { id: 7, title: 'Upload completion certificates', dueIn: 5, priority: 'low', kind: 'job', reference: 'J004467', from: 'You', done: false },
  { id: 8, title: 'Raise the first-hour invoice', dueIn: -3, priority: 'normal', kind: 'invoice', reference: 'J004470', from: 'Accounts', done: true },
]

type Filter = 'open' | 'today' | 'overdue' | 'done'

const dueLabel = (dueIn: number) => {
  if (dueIn < -1) return `${-dueIn} days overdue`
  if (dueIn === -1) return 'Yesterday'
  if (dueIn === 0) return 'Today'
  if (dueIn === 1) return 'Tomorrow'
  const d = new Date()
  d.setDate(d.getDate() + dueIn)
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

const dueTone = (t: Task) => (t.done ? 'done' : t.dueIn < 0 ? 'overdue' : t.dueIn === 0 ? 'today' : 'later')

const TaskListPreview = () => {
  const [tasks, setTasks] = useState<Task[]>(SAMPLE_TASKS)
  const [filter, setFilter] = useState<Filter>('open')
  const [draft, setDraft] = useState('')

  const counts = useMemo(
    () => ({
      open: tasks.filter((t) => !t.done).length,
      today: tasks.filter((t) => !t.done && t.dueIn === 0).length,
      overdue: tasks.filter((t) => !t.done && t.dueIn < 0).length,
      done: tasks.filter((t) => t.done).length,
    }),
    [tasks]
  )

  const shown = tasks
    .filter((t) =>
      filter === 'open'
        ? !t.done
        : filter === 'today'
        ? !t.done && t.dueIn === 0
        : filter === 'overdue'
        ? !t.done && t.dueIn < 0
        : t.done
    )
    // Most pressing first: overdue, then today, then by date; high priority leads ties.
    .sort((a, b) => a.dueIn - b.dueIn || (a.priority === 'high' ? -1 : b.priority === 'high' ? 1 : 0))

  const toggle = (id: number) => setTasks((all) => all.map((t) => (t.id === id ? { ...t, done: !t.done } : t)))

  const add = () => {
    const title = draft.trim()
    if (!title) return
    setTasks((all) => [
      { id: Date.now(), title, dueIn: 0, priority: 'normal', kind: 'job', reference: '', from: 'You', done: false },
      ...all,
    ])
    setDraft('')
    if (filter === 'done' || filter === 'overdue') setFilter('open')
  }

  const tabs: { key: Filter; label: string }[] = [
    { key: 'open', label: 'Open' },
    { key: 'today', label: 'Due today' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'done', label: 'Done' },
  ]

  return (
    <article className='hp-card hp-tasks'>
      <div className='hp-tasks__head'>
        <div>
          <p className='hp-eyebrow'>
            My tasks <span className='hp-sample'>Sample data</span>
          </p>
          <h3 className='hp-tasks__title'>
            {counts.open} open
            {counts.overdue > 0 && <span className='hp-tasks__overdue'> · {counts.overdue} overdue</span>}
          </h3>
        </div>
        <div className='hp-segment' role='tablist' aria-label='Show tasks'>
          {tabs.map((t) => (
            <button
              key={t.key}
              type='button'
              role='tab'
              aria-selected={filter === t.key}
              className={`hp-segment__btn${filter === t.key ? ' hp-segment__btn--on' : ''}`}
              onClick={() => setFilter(t.key)}
            >
              {t.label}
              {counts[t.key] > 0 && <span className='hp-segment__count'>{counts[t.key]}</span>}
            </button>
          ))}
        </div>
      </div>

      <form
        className='hp-tasks__add'
        onSubmit={(e) => {
          e.preventDefault()
          add()
        }}
      >
        <FaPlus aria-hidden='true' className='hp-tasks__add-icon' />
        <input
          className='hp-tasks__add-input'
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder='Add a task for today…'
          aria-label='New task'
        />
        {draft.trim() && (
          <button type='submit' className='hp-tasks__add-btn'>
            Add
          </button>
        )}
      </form>

      {shown.length === 0 ? (
        <div className='hp-tasks__empty'>
          <span className='hp-tasks__empty-icon' aria-hidden='true'>
            <FaCheck />
          </span>
          {filter === 'done' ? 'Nothing ticked off yet.' : 'All clear - nothing here.'}
        </div>
      ) : (
        <ul className='hp-tasks__list'>
          {shown.map((t) => (
            <li key={t.id} className={`hp-task${t.done ? ' hp-task--done' : ''}`}>
              <button
                type='button'
                className='hp-task__check'
                onClick={() => toggle(t.id)}
                aria-label={t.done ? `Mark "${t.title}" as not done` : `Mark "${t.title}" as done`}
                aria-pressed={t.done}
              >
                {t.done && <FaCheck aria-hidden='true' />}
              </button>

              <div className='hp-task__body'>
                <p className='hp-task__title'>
                  {t.priority === 'high' && !t.done && (
                    <FaFlag className='hp-task__flag' aria-label='High priority' />
                  )}
                  {t.title}
                </p>
                <div className='hp-task__meta'>
                  {t.reference && (
                    <span className='hp-chip'>
                      <span aria-hidden='true'>{KIND_ICON[t.kind]}</span>
                      {t.reference}
                    </span>
                  )}
                  <span className='hp-task__from'>{t.from === 'You' ? 'Added by you' : `From ${t.from}`}</span>
                </div>
              </div>

              <span className={`hp-due hp-due--${dueTone(t)}`}>
                <FaRegCalendar aria-hidden='true' />
                {dueLabel(t.dueIn)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  )
}

export default TaskListPreview
